import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  RequestQuote as RequestQuoteIcon,
  Drafts as DraftsIcon,
  Send as SendIcon,
  QuestionAnswer as ResponsesIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { format, isPast, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { RFQ, RFQStatus } from '../types';
import { rfqService } from '../services/rfqService';
import { colors } from '../theme';

// ============================================================================
// Configuración de colores y labels por estado
// ============================================================================

const rfqStatusColors: Record<RFQStatus, string> = {
  draft: '#9e9e9e',
  sent: '#2196f3',
  responses: '#4caf50',
  closed: '#616161',
};

const rfqStatusLabels: Record<RFQStatus, string> = {
  draft: 'Borrador',
  sent: 'Enviada',
  responses: 'Respondida',
  closed: 'Cerrada',
};

const RFQList: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [rfqs, setRfqs] = useState<RFQ[]>([]);
  const [filteredRfqs, setFilteredRfqs] = useState<RFQ[]>([]);

  // Filters
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rfqToDelete, setRfqToDelete] = useState<RFQ | null>(null);

  // =========================================================================
  // Carga inicial de datos
  // =========================================================================

  useEffect(() => {
    // Inicializar mock data si localStorage está vacío
    rfqService.initializeIfEmpty();
    loadRfqs();

    // Escuchar cambios en RFQs
    const handleRfqUpdate = () => loadRfqs();
    window.addEventListener('rfqsUpdated', handleRfqUpdate);
    return () => window.removeEventListener('rfqsUpdated', handleRfqUpdate);
  }, []);

  const loadRfqs = () => {
    const all = rfqService.getAll();
    setRfqs(all);
  };

  // =========================================================================
  // Filtrado
  // =========================================================================

  useEffect(() => {
    let result = [...rfqs];

    // Filtro por estado
    if (statusFilter !== 'all') {
      result = result.filter(r => r.status === statusFilter);
    }

    // Filtro por categoría
    if (categoryFilter !== 'all') {
      result = result.filter(r => r.mainCategory === categoryFilter);
    }

    // Filtro por búsqueda (número, título, categoría, proveedores)
    if (searchText.trim()) {
      const search = searchText.toLowerCase().trim();
      result = result.filter(r =>
        r.number.toLowerCase().includes(search) ||
        r.title.toLowerCase().includes(search) ||
        r.mainCategory.toLowerCase().includes(search) ||
        r.suppliers.some(s => s.supplierName.toLowerCase().includes(search))
      );
    }

    // Ordenar por fecha de creación descendente
    result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    setFilteredRfqs(result);
    setPage(0); // Reset pagination on filter change
  }, [rfqs, statusFilter, categoryFilter, searchText]);

  // =========================================================================
  // KPIs
  // =========================================================================

  const kpiTotal = rfqs.length;
  const kpiDrafts = rfqs.filter(r => r.status === 'draft').length;
  const kpiSent = rfqs.filter(r => r.status === 'sent').length;
  const kpiResponses = rfqs.filter(r => r.status === 'responses').length;

  // =========================================================================
  // Handlers
  // =========================================================================

  const handleDeleteClick = (rfq: RFQ) => {
    setRfqToDelete(rfq);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (rfqToDelete) {
      rfqService.delete(rfqToDelete.id);
      loadRfqs();
    }
    setDeleteDialogOpen(false);
    setRfqToDelete(null);
  };

  // =========================================================================
  // Helper para formatear fecha
  // =========================================================================

  const formatDate = (dateStr: string) => {
    try {
      return format(parseISO(dateStr), 'dd/MM/yyyy', { locale: es });
    } catch {
      return '—';
    }
  };

  // =========================================================================
  // Categorías únicas en los RFQs existentes (para el filtro)
  // =========================================================================

  const rfqCategories = Array.from(new Set(rfqs.map(r => r.mainCategory))).sort();

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <div>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Compulsas & RFQ
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Gestión de solicitudes de cotización
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/rfq/new')}
          sx={{
            bgcolor: colors.brand.red,
            '&:hover': { bgcolor: colors.primary.dark },
          }}
        >
          Nueva Compulsa
        </Button>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <RequestQuoteIcon sx={{ fontSize: 32, color: colors.brand.blue, mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {kpiTotal}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Compulsas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <DraftsIcon sx={{ fontSize: 32, color: rfqStatusColors.draft, mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {kpiDrafts}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Borradores
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <SendIcon sx={{ fontSize: 32, color: rfqStatusColors.sent, mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {kpiSent}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enviadas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <ResponsesIcon sx={{ fontSize: 32, color: rfqStatusColors.responses, mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {kpiResponses}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Con Respuestas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por número, título, categoría o proveedor..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              select
              label="Estado"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="draft">Borrador</MenuItem>
              <MenuItem value="sent">Enviada</MenuItem>
              <MenuItem value="responses">Respondida</MenuItem>
              <MenuItem value="closed">Cerrada</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              select
              label="Categoría"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="all">Todas</MenuItem>
              {rfqCategories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
              {filteredRfqs.length} resultado{filteredRfqs.length !== 1 ? 's' : ''}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Número</TableCell>
                <TableCell>Título</TableCell>
                <TableCell>Categoría</TableCell>
                <TableCell align="center">Proveedores</TableCell>
                <TableCell align="center">Respuestas</TableCell>
                <TableCell>Fecha Límite</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRfqs
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((rfq) => {
                  const deadlinePast = isPast(parseISO(rfq.deadline));
                  const responsesCount = rfq.responses.length;
                  const suppliersCount = rfq.suppliers.length;

                  return (
                    <TableRow
                      key={rfq.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/rfq/${rfq.id}`)}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: colors.brand.blue,
                          }}
                        >
                          {rfq.number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {rfq.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={rfq.mainCategory}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={suppliersCount}
                          size="small"
                          sx={{
                            bgcolor: colors.brand.blue,
                            color: 'white',
                            fontWeight: 600,
                            minWidth: 32,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: responsesCount > 0 ? 600 : 400,
                            color: responsesCount > 0 ? colors.success.main : 'text.secondary',
                          }}
                        >
                          {responsesCount}/{suppliersCount}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: deadlinePast && rfq.status !== 'closed' ? colors.error.main : 'text.primary',
                            fontWeight: deadlinePast && rfq.status !== 'closed' ? 600 : 400,
                          }}
                        >
                          {formatDate(rfq.deadline)}
                          {deadlinePast && rfq.status !== 'closed' && (
                            <Typography component="span" variant="caption" sx={{ ml: 0.5, color: colors.error.main }}>
                              (vencida)
                            </Typography>
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={rfqStatusLabels[rfq.status]}
                          size="small"
                          sx={{
                            bgcolor: rfqStatusColors[rfq.status],
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          <Tooltip title="Ver detalle">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/rfq/${rfq.id}`);
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {rfq.status === 'draft' && (
                            <Tooltip title="Eliminar">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(rfq);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {filteredRfqs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <RequestQuoteIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      No se encontraron compulsas
                    </Typography>
                    <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                      {rfqs.length === 0
                        ? 'Cree su primera compulsa usando el botón "Nueva Compulsa"'
                        : 'Intente modificar los filtros de búsqueda'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredRfqs.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Eliminar Compulsa</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro que desea eliminar la compulsa <strong>{rfqToDelete?.number}</strong> — "{rfqToDelete?.title}"?
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RFQList;
